import { api, cacheShortTimer, cacheTimer, thunks } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  AutoscalingTypes,
  DeployServiceSizingPolicy,
  DeployServiceSizingPolicyScheduleV2,
  LinkResponse,
  ServiceSizingPolicyScheduleTypeV2,
} from "@app/types";
import { selectServiceById } from "../service";

// ---------------------------------------------------------------------------
// v2 (scheduled HAS) — this section is unreleased and unused by the app.
//
// Three assumptions are UNCONFIRMED against deploy-api: reads arrive as
// `_embedded.service_sizing_policy_schedules[]`, writes go back under the key
// `schedule_data` (a virtual attribute over the child table), and each row
// carries a `type` of "daily" or "range".
// ---------------------------------------------------------------------------

export interface DeployServiceSizingPolicyScheduleResponseV2 {
  id: number | undefined;
  service_sizing_policy_id?: number | null;
  type?: ServiceSizingPolicyScheduleTypeV2;
  start_day: number;
  start_time: string;
  end_day: number;
  end_time: string;
  created_at: string;
  updated_at: string;
  _links: {
    service_sizing_policy?: LinkResponse;
  };
  _type: "service_sizing_policy_schedule";
}

export interface ServiceSizingPolicyScheduleEditPropsV2 {
  id?: string;
  type: ServiceSizingPolicyScheduleTypeV2;
  startDay: number;
  startTime: string;
  endDay: number;
  endTime: string;
}

export const defaultServiceSizingPolicyScheduleResponseV2 = (
  s: Partial<DeployServiceSizingPolicyScheduleResponseV2> = {},
): DeployServiceSizingPolicyScheduleResponseV2 => {
  const now = new Date().toISOString();
  return {
    id: undefined,
    service_sizing_policy_id: null,
    type: "daily",
    start_day: 1,
    start_time: "00:00",
    end_day: 1,
    end_time: "00:00",
    created_at: now,
    updated_at: now,
    _type: "service_sizing_policy_schedule",
    ...s,
    _links: { ...s._links },
  };
};

export const deserializeDeployServiceSizingPolicyScheduleV2 = (
  payload: DeployServiceSizingPolicyScheduleResponseV2,
): DeployServiceSizingPolicyScheduleV2 => {
  const fromLink = extractIdFromLink(payload._links?.service_sizing_policy);
  const fromColumn = payload.service_sizing_policy_id;

  return {
    id: payload.id == null ? "" : `${payload.id}`,
    serviceSizingPolicyId:
      fromLink || (fromColumn == null ? "" : `${fromColumn}`),
    // A row saved before ranges existed repeats on its start day.
    type: payload.type ?? "daily",
    startDay: payload.start_day,
    startTime: payload.start_time,
    endDay: payload.end_day,
    endTime: payload.end_time,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

const serializeScheduleIdV2 = (id: string | undefined) => {
  if (!id) {
    return {};
  }
  return { id: Number(id) };
};

export const serializeServiceSizingPolicyScheduleV2 = (
  payload: ServiceSizingPolicyScheduleEditPropsV2,
) => ({
  ...serializeScheduleIdV2(payload.id),
  type: payload.type,
  start_day: payload.startDay,
  start_time: payload.startTime,
  end_day: payload.endDay,
  end_time: payload.endTime,
});

const serializeLabelV2 = (label: string | undefined) => {
  if (label === undefined) {
    return {};
  }
  return { label };
};

const serializeScheduleDataV2 = (
  schedules: ServiceSizingPolicyScheduleEditPropsV2[] | undefined,
) => {
  if (schedules === undefined) {
    return {};
  }
  return {
    schedule_data: schedules.map(serializeServiceSizingPolicyScheduleV2),
  };
};

export const serializeServiceSizingPolicyFieldsV2 = (payload: {
  label?: string;
  schedulesV2?: ServiceSizingPolicyScheduleEditPropsV2[];
}) => ({
  ...serializeLabelV2(payload.label),
  ...serializeScheduleDataV2(payload.schedulesV2),
});

export const selectServiceSizingPolicySchedulesV2ById =
  schema.serviceSizingPolicySchedulesV2.selectById;

export const selectServiceSizingPolicySchedulesV2AsList =
  schema.serviceSizingPolicySchedulesV2.selectTableAsList;

export const selectServiceSizingPolicySchedulesV2ByPolicyId = createSelector(
  schema.serviceSizingPolicySchedulesV2.selectTableAsList,
  (_: WebState, p: { policyId: string }) => p.policyId,
  (schedules, policyId) =>
    schedules
      .filter((s) => s.serviceSizingPolicyId === policyId)
      .sort(
        (a, b) =>
          a.startDay - b.startDay || a.startTime.localeCompare(b.startTime),
      ),
);

// Derived, not a new `autoscaling` enum value — that would invalidate every
// existing `autoscaling === "horizontal"` check.
export const selectIsScheduledPolicyV2ByPolicyId = createSelector(
  selectServiceSizingPolicySchedulesV2ByPolicyId,
  (schedules) => schedules.length > 0,
);

const WEEK_MINUTES = 7 * 24 * 60;

// Minutes since Monday 00:00 UTC, as the half-open range [start, end).
export type WeekSegment = [startMinute: number, endMinute: number];

// Everything the overlap maths needs: a window, plus an optional id so an edit
// can recognise its own saved row. `type` is irrelevant here — daily and range
// windows both land on the same weekly circle.
type ScheduleWindowV2 = Omit<ServiceSizingPolicyScheduleEditPropsV2, "type">;

const toWeekMinutes = (day: number, time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return (day - 1) * 24 * 60 + hours * 60 + minutes;
};

// A window sits on a weekly circle. When end is not after start it wraps past
// Sunday midnight and covers two segments, so end === start means the whole
// week.
export const toWeekSegments = (schedule: ScheduleWindowV2): WeekSegment[] => {
  const start = toWeekMinutes(schedule.startDay, schedule.startTime);
  const end = toWeekMinutes(schedule.endDay, schedule.endTime);

  if (start < end) {
    return [[start, end]];
  }
  return [
    [start, WEEK_MINUTES],
    [0, end],
  ];
};

// Overlap of a week segment with one day's window, as day-relative minutes.
// Half-open: a segment ending at the day boundary isn't on the next day.
export const intersectDay = (
  segment: WeekSegment,
  dayStartMinute: number,
): WeekSegment | null => {
  const dayEndMinute = dayStartMinute + 24 * 60;
  const start = Math.max(segment[0], dayStartMinute);
  const end = Math.min(segment[1], dayEndMinute);
  if (start >= end) return null;
  return [start - dayStartMinute, end - dayStartMinute];
};

const segmentsOverlap = (a: WeekSegment, b: WeekSegment) => {
  const [aStart, aEnd] = a;
  const [bStart, bEnd] = b;
  return aStart < bEnd && bStart < aEnd;
};

const windowsOverlap = (a: WeekSegment[], b: WeekSegment[]) =>
  a.some((aSegment) =>
    b.some((bSegment) => segmentsOverlap(aSegment, bSegment)),
  );

// Returns the existing schedules the candidate collides with, or [] when it is
// safe to save.
export function findScheduleCollisionsV2<T extends ScheduleWindowV2>(
  candidate: ScheduleWindowV2,
  existing: T[],
): T[] {
  const candidateSegments = toWeekSegments(candidate);

  // An edit carries the id of the row it replaces, which is not a collision.
  const isOwnSavedRow = (schedule: T) =>
    Boolean(candidate.id) && schedule.id === candidate.id;

  return existing.filter(
    (schedule) =>
      !isOwnSavedRow(schedule) &&
      windowsOverlap(candidateSegments, toWeekSegments(schedule)),
  );
}

/**
 * Every policy id caught in at least one overlap, for flagging conflicting
 * rows in the UI. Bounded by the 7-policy-per-service limit, so the pairwise
 * scan is cheap enough to run on each keystroke.
 */
export function findConflictingPolicyIdsV2(
  schedules: DeployServiceSizingPolicyScheduleV2[],
): Set<string> {
  const conflicting = new Set<string>();
  for (const schedule of schedules) {
    for (const other of findScheduleCollisionsV2(schedule, schedules)) {
      conflicting.add(schedule.serviceSizingPolicyId);
      conflicting.add(other.serviceSizingPolicyId);
    }
  }
  return conflicting;
}

// --------------------------- end v2 ----------------------------------------

export interface DeployServiceSizingPolicyResponse {
  id: number | undefined;
  scaling_enabled: boolean;
  autoscaling: AutoscalingTypes;
  default_policy: boolean;
  metric_lookback_seconds: number;
  percentile: number;
  post_scale_up_cooldown_seconds: number;
  post_scale_down_cooldown_seconds: number;
  post_release_cooldown_seconds: number;
  mem_cpu_ratio_r_threshold: number;
  mem_cpu_ratio_c_threshold: number;
  mem_scale_up_threshold: number;
  mem_scale_down_threshold: number;
  minimum_memory: number;
  maximum_memory: number | null;
  min_containers: number | null;
  max_containers: number | null;
  min_cpu_threshold: number | null;
  max_cpu_threshold: number | null;
  scale_up_step: number;
  scale_down_step: number;
  use_horizontal_scale: boolean;
  // v2
  label?: string;
  created_at: string;
  updated_at: string;
  _links: {
    account: LinkResponse;
  };
  _embedded?: {
    service_sizing_policy_schedules?:
      | DeployServiceSizingPolicyScheduleResponseV2[]
      | null;
  };
  _type: "service_sizing_policy";
}

export const defaultServiceSizingPolicyResponse = (
  s: Partial<DeployServiceSizingPolicyResponse> = {},
): DeployServiceSizingPolicyResponse => {
  const now = new Date().toISOString();
  return {
    id: undefined,
    scaling_enabled: false,
    autoscaling: "vertical",
    default_policy: false,
    metric_lookback_seconds: 300,
    percentile: 99,
    post_scale_up_cooldown_seconds: 60,
    post_scale_down_cooldown_seconds: 300,
    post_release_cooldown_seconds: 60,
    mem_cpu_ratio_r_threshold: 4,
    mem_cpu_ratio_c_threshold: 2,
    mem_scale_up_threshold: 0.9,
    mem_scale_down_threshold: 0.75,
    minimum_memory: 2048,
    maximum_memory: null,
    min_containers: 2,
    max_containers: 4,
    min_cpu_threshold: 0.1,
    max_cpu_threshold: 0.9,
    scale_up_step: 1,
    scale_down_step: 1,
    use_horizontal_scale: false,
    // v2
    label: "",
    created_at: now,
    updated_at: now,
    _type: "service_sizing_policy",
    ...s,
    _links: { account: defaultHalHref(), ...s._links },
  };
};

export const deserializeDeployServiceSizingPolicy = (
  payload: DeployServiceSizingPolicyResponse,
): DeployServiceSizingPolicy => {
  const links = payload._links;
  const environmentId = extractIdFromLink(links.account);

  return {
    id: `${payload.id}`,
    environmentId,
    scalingEnabled: payload.scaling_enabled,
    autoscaling: payload.autoscaling,
    defaultPolicy: payload.default_policy,
    metricLookbackSeconds: payload.metric_lookback_seconds,
    percentile: payload.percentile,
    postScaleUpCooldownSeconds: payload.post_scale_up_cooldown_seconds,
    postScaleDownCooldownSeconds: payload.post_scale_down_cooldown_seconds,
    postReleaseCooldownSeconds: payload.post_release_cooldown_seconds,
    memCpuRatioRThreshold: payload.mem_cpu_ratio_r_threshold,
    memCpuRatioCThreshold: payload.mem_cpu_ratio_c_threshold,
    memScaleUpThreshold: payload.mem_scale_up_threshold,
    memScaleDownThreshold: payload.mem_scale_down_threshold,
    minimumMemory: payload.minimum_memory,
    maximumMemory: payload.maximum_memory,
    minContainers: payload.min_containers,
    maxContainers: payload.max_containers,
    scaleUpStep: payload.scale_up_step,
    scaleDownStep: payload.scale_down_step,
    minCpuThreshold: payload.min_cpu_threshold,
    maxCpuThreshold: payload.max_cpu_threshold,
    useHorizontalScale: payload.use_horizontal_scale,
    // v2
    label: payload.label ?? "",
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

export const serviceSizingPolicyEntities = {
  service_sizing_policy: defaultEntity({
    id: "service_sizing_policy",
    deserialize: deserializeDeployServiceSizingPolicy,
    save: schema.serviceSizingPolicies.add,
  }),
  // v2
  service_sizing_policy_schedule: defaultEntity({
    id: "service_sizing_policy_schedule",
    deserialize: deserializeDeployServiceSizingPolicyScheduleV2,
    save: schema.serviceSizingPolicySchedulesV2.add,
  }),
};

export const selectServiceSizingPolicyById =
  schema.serviceSizingPolicies.selectById;
export const selectServiceSizingPolicyByServiceId = createSelector(
  selectServiceById,
  schema.serviceSizingPolicies.selectTableAsList,
  (service, policies) =>
    policies.find((p) => p.id === service.serviceSizingPolicyId) ||
    schema.serviceSizingPolicies.empty,
);

export const selectAutoscalingEnabledById = createSelector(
  selectServiceSizingPolicyById,
  (policy) => policy.scalingEnabled,
);
export const selectAutoscalingEnabledByServiceId = createSelector(
  selectServiceSizingPolicyByServiceId,
  (policy) => policy.scalingEnabled,
);

export const fetchServiceSizingPolicies = api.get(
  "/service_sizing_policies?per_page=5000",
  {
    supervisor: cacheTimer(),
  },
);

export const fetchServiceSizingPoliciesByEnvironmentId = api.get<{
  id: string;
}>("/accounts/:id/service_sizing_policies", { supervisor: cacheShortTimer() });
export const fetchServiceSizingPoliciesByServiceId = api.get<{
  serviceId: string;
}>("/services/:serviceId/service_sizing_policies", {
  supervisor: cacheShortTimer(),
});

export interface ServiceSizingPolicyEditProps {
  scalingEnabled: boolean;
  autoscaling: AutoscalingTypes;
  metricLookbackSeconds: number;
  percentile: number;
  postScaleUpCooldownSeconds: number;
  postScaleDownCooldownSeconds: number;
  postReleaseCooldownSeconds: number;
  memCpuRatioRThreshold: number;
  memCpuRatioCThreshold: number;
  memScaleUpThreshold: number;
  memScaleDownThreshold: number;
  minimumMemory: number;
  maximumMemory: number | null;
  minContainers: number | null;
  maxContainers: number | null;
  minCpuThreshold: number | null;
  maxCpuThreshold: number | null;
  scaleUpStep: number;
  scaleDownStep: number;
  useHorizontalScale: boolean;
  // TODO - label field to add, this will alter serialization, so avoiding
  schedulesV2?: ServiceSizingPolicyScheduleEditPropsV2[];
}

const serializeServiceSizingPolicyEditProps = (
  payload: ServiceSizingPolicyEditProps,
) => ({
  scaling_enabled: payload.scalingEnabled,
  autoscaling: payload.autoscaling,
  metric_lookback_seconds: payload.metricLookbackSeconds,
  percentile: payload.percentile,
  post_scale_up_cooldown_seconds: payload.postScaleUpCooldownSeconds,
  post_scale_down_cooldown_seconds: payload.postScaleDownCooldownSeconds,
  post_release_cooldown_seconds: payload.postReleaseCooldownSeconds,
  mem_cpu_ratio_r_threshold: payload.memCpuRatioRThreshold,
  mem_cpu_ratio_c_threshold: payload.memCpuRatioCThreshold,
  mem_scale_up_threshold: payload.memScaleUpThreshold,
  mem_scale_down_threshold: payload.memScaleDownThreshold,
  minimum_memory: payload.minimumMemory,
  maximum_memory: payload.maximumMemory || null, // 0 => null
  min_containers: payload.minContainers,
  max_containers: payload.maxContainers,
  min_cpu_threshold: payload.minCpuThreshold,
  max_cpu_threshold: payload.maxCpuThreshold,
  scale_up_step: payload.scaleUpStep,
  scale_down_step: payload.scaleDownStep,
  use_horizontal_scale: payload.useHorizontalScale,
});

export interface ModifyServiceSizingPolicyProps
  extends ServiceSizingPolicyEditProps {
  id: string;
  serviceId: string;
}

export const createServiceSizingPolicyByServiceId = api.post<
  ModifyServiceSizingPolicyProps,
  DeployServiceSizingPolicyResponse
>(["/services/:serviceId/service_sizing_policies"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeServiceSizingPolicyEditProps(ctx.payload)),
  });
  yield* next();
});

export const updateServiceSizingPolicyByServiceId = api.put<
  ModifyServiceSizingPolicyProps,
  DeployServiceSizingPolicyResponse
>(["/services/:serviceId/service_sizing_policies"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeServiceSizingPolicyEditProps(ctx.payload)),
  });
  yield* next();
});

export const deleteServiceSizingPolicyByServiceId = api.delete<{
  serviceId: string;
}>(["/services/:serviceId/service_sizing_policy"], function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }
  ctx.loader = { message: "Policy changes saved" };
});

export const modifyServiceSizingPolicy =
  thunks.create<ModifyServiceSizingPolicyProps>(
    "modify-service-sizing-policy",
    function* (ctx, next) {
      yield* schema.update(schema.loaders.start({ id: ctx.name }));
      const nextPolicy = ctx.payload;
      const updateCtx = nextPolicy.id
        ? yield* updateServiceSizingPolicyByServiceId.run(nextPolicy)
        : yield* createServiceSizingPolicyByServiceId.run(nextPolicy);

      yield* next();

      if (updateCtx.json.ok) {
        yield* schema.update([
          schema.services.patch({
            [nextPolicy.serviceId]: {
              serviceSizingPolicyId: `${updateCtx.json.value.id}`,
            },
          }),
          schema.loaders.success({
            id: ctx.name,
            message: "Policy changes saved",
          }),
        ]);
      } else {
        const data = updateCtx.json.error as Error;
        yield* schema.update(
          schema.loaders.error({ id: ctx.name, message: data.message }),
        );
      }
    },
  );
